/**
 * THE ROW MODEL EVERY EXPORT WRITES.
 *
 * ONE conversion, upstream of all three formats, so a CSV, a workbook and a PDF
 * of the same view cannot disagree about what a row says. The screens this
 * replaces had four separate mappers — one per book, inside the XLS writer only
 * — so the spreadsheet and the table on screen were two independent opinions
 * about the same data.
 *
 * ── THE CAVEATS TRAVEL WITH THE NUMBERS ──
 *
 * A file outlives the page it came from, and nobody who receives it can see what
 * was on screen. So the header block carries every applied filter, the scope
 * sentence, the enforcement warning when there is one, the truncation banner
 * when there is one, and a generated-at timestamp. A file that carries the
 * figures and drops the sentence saying they cover every building in the
 * organisation is the most dangerous artefact this screen can produce.
 */
import {
  AnalyticsBatchResult,
  isBatchFailure,
} from '../../../../../api/analytics-client';
import { format } from '../charts/chart-theme';
import { BOOK_LABELS, DashboardFilter, FilterMeta } from '../state/filter-model';
import { describeFilter } from '../state/filter-chips';
import { DashboardData } from '../data/dashboard-data';
import { countOf } from '../../../../../function-utils/plural';
import { RecordRow, RecordsClock } from '../data/records-api';
import {
  dateTimeIn,
  deviceZone,
  formatDayKey,
  isDayKey,
  offsetAt,
  usableZone,
  wallTextIn,
  zoneLabel,
  zoneNote,
} from '../../../../../function-utils/building-time';
import { RECORD_COLUMNS, cellText, columnsFor } from '../tabs/records-columns';
import { panelTitles } from '../data/panel-plan';

export interface ExportSection {
  title: string;
  /** A sentence under the title — a caveat, a note, a warning. */
  caption?: string;
  header: string[];
  rows: string[][];
}

export interface ExportModel {
  filename: string;
  title: string;
  generatedAt: string;
  /** "7 Oct 2026, 14:05 — Auckland time (UTC+13:00)": when the file was made,
   *  on the reader's clock, WITH the zone (R-T7). The PDF cover prints this. */
  generatedLabel: string;
  /** The cover block: every applied filter, plus scope and enforcement. */
  meta: string[][];
  /** Non-empty when the answer is a prefix rather than the whole result. */
  truncation?: string;
  sections: ExportSection[];
}

const slug = (value: string): string =>
  value
    .normalize('NFKD')
    // eslint-disable-next-line no-control-regex
    .replace(/[̀-ͯ]/g, '')
    .replace(/[^a-zA-Z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .toLowerCase() || 'all';

const stamp = (at: Date): string =>
  `${at.getFullYear()}${String(at.getMonth() + 1).padStart(2, '0')}${String(
    at.getDate()
  ).padStart(2, '0')}-${String(at.getHours()).padStart(2, '0')}${String(
    at.getMinutes()
  ).padStart(2, '0')}`;

export interface ExportInput {
  filter: DashboardFilter;
  filterMeta?: FilterMeta;
  data: DashboardData;
  results: Record<string, AnalyticsBatchResult | undefined>;
  /** All matching rows, not just the page on screen. */
  rows: RecordRow[];
  truncated: boolean;
  rowLimit: number | null;
  columns: string[] | null;
  /** Restrict the model to one panel, for a per-panel CSV. */
  panelId?: string;
  now?: Date;
  /** The clock the Records answer is on, from its `X-Report-*` headers. Absent
   *  for a per-panel export, which carries no rows. */
  clock?: RecordsClock;
}

/* ════════════ A FILE THE READER DOWNLOADS IS ON THE READER'S CLOCK ════════════
 *
 * R-T12: a file somebody downloads for themselves is on THEIR clock, with the
 * zone named. The server has already put every row there and says which clock
 * it is; nothing below decides a zone for itself.
 *
 *   - the Records rows' `createdAt` is zone-less TEXT, already on that clock —
 *     written to the file as given;
 *   - the other date columns are instants and are written as text in the same
 *     zone (they used to leave as raw ISO, i.e. UTC, beside local text);
 *   - the cover names the clock with the SERVER's label, verbatim, and prints
 *     the period as the day keys the server resolved;
 *   - "Building time" is an EXTRA column, offered only when some row's building
 *     is on a different clock from the file's AT THAT ROW'S INSTANT.
 */
const DAY_FULL: Intl.DateTimeFormatOptions = {
  day: 'numeric',
  month: 'short',
  year: 'numeric',
};

/** Is this row's building on another clock than the file's, when it happened? */
const onAnotherClock = (row: RecordRow, fileZone: string): boolean => {
  const building = usableZone(row?.tz);
  const at = Date.parse(String(row?.utcCreatedAt ?? ''));
  if (!building || !fileZone || Number.isNaN(at)) return false;

  return offsetAt(at, building) !== offsetAt(at, fileZone);
};

/** "2026-10-07 14:05:12 (Toronto)" — the main time column's shape, in the
 *  row's own building's zone, with the city. */
const buildingTimeCell = (row: RecordRow): string => {
  const building = usableZone(row?.tz);
  const text = building ? wallTextIn(row?.utcCreatedAt, building, { seconds: true }) : '';

  return text ? `${text} (${zoneLabel(building)})` : '';
};

const n = (v: number | null | undefined): string => (v == null ? '' : String(v));

const figuresSection = (data: ExportInput['data']): ExportSection[] => {
  const overview = data.overview.data;
  if (!overview) return [];

  return [
    {
      title: 'Figures for this period',
      header: ['Figure', 'Value', 'Previous', 'Change', 'Counted'],
      rows: Object.entries(overview.kpis).map(([key, kpi]) => [
        key,
        String(kpi.value),
        kpi.compareValue == null ? '' : String(kpi.compareValue),
        kpi.delta == null ? '' : String(kpi.delta),
        kpi.kind,
      ]),
    },
  ];
};

/** The panels fed by their own slice, keyed by the id their tab passes. */
const SLICE_PANELS: Record<string, (data: ExportInput['data']) => ExportSection[]> = {
  'all-figures': figuresSection,
  shelf: data => {
    const latency = data.latency.data;

    return [
      ...figuresSection(data),
      ...(latency
        ? [
            {
              title: 'What missed the threshold',
              header: ['Missed', 'Of', 'Threshold (hours)'],
              rows: [[n(latency.breached), n(latency.count), n(latency.thresholdHours)]],
            },
          ]
        : []),
    ];
  },
  dwell: data => {
    const l = data.latency.data;
    if (!l) return [];

    return [
      {
        title: l.label || 'Dwell',
        caption: `${l.subject}, measured from ${l.measuredFrom} to ${l.measuredTo}, in ${l.unit}.`,
        header: ['Count', 'Median (p50)', 'p90', 'p99', 'Shortest', 'Longest', 'Threshold (hours)', 'Missed'],
        rows: [[n(l.count), n(l.p50), n(l.p90), n(l.p99), n(l.min), n(l.max), n(l.thresholdHours), n(l.breached)]],
      },
    ];
  },
  quality: data => {
    const q = data.quality.data;
    if (!q) return [];

    return [
      {
        title: 'Data quality',
        caption: `${q.entries} entries in the window.`,
        header: ['Measure', 'Count', 'Out of', 'Ratio'],
        rows: q.ratios.map(r => [r.label, n(r.numerator), n(r.denominator), n(r.ratio)]),
      },
      {
        title: 'Data quality — entries by source',
        header: ['Source', 'Entries'],
        rows: Object.entries(q.bySource).map(([source, count]) => [source, n(count)]),
      },
    ];
  },
  turnover: data => {
    const t = data.turnover.data;
    if (!t) return [];

    return [
      {
        title: `Turnover by ${t.bucket}`,
        header: ['Period', 'Move-ins', 'Move-outs', 'Net'],
        rows: t.series.map(b => [b.bucket, n(b.moveIns), n(b.moveOuts), n(b.net)]),
      },
      {
        title: 'Turnover — move-out reasons',
        caption: `${t.reasonsRecorded} move-outs recorded a reason.`,
        header: ['Reason', 'Count'],
        rows: t.reasons.map(r => [r.reason, n(r.count)]),
      },
      {
        title: 'Turnover — occupancy by building',
        header: ['Building', 'Units', ...t.classes],
        rows: t.occupancy.map(o => [o.building, n(o.units), ...t.classes.map(c => n(o.classes[c]))]),
      },
    ];
  },
  rhythm: data => {
    const r = data.rhythm.data;
    if (!r) return [];

    return [
      {
        title: r.label || 'Rhythm',
        caption: `${r.total} in total. ${r.weekdayBasis}`,
        header: ['Weekday', 'Hour', 'Value'],
        rows: r.cells.map(c => [c.weekdayLabel, String(c.hour), n(c.value)]),
      },
    ];
  },
};

export const buildExportModel = (input: ExportInput): ExportModel => {
  const { filter, filterMeta, data, results, rows, columns } = input;
  const at = input.now || new Date();
  const scope = data.batch.data?.scope || data.overview.data?.scope;
  const window = data.batch.data?.window || data.overview.data?.window;

  const scopeSlug = slug(
    filter.buildingId != null
      ? filterMeta?.buildings.find(b => b.id === filter.buildingId)?.name || 'building'
      : filter.portfolioId != null
      ? filterMeta?.portfolios.find(p => p.id === filter.portfolioId)?.name || 'portfolio'
      : 'all-buildings'
  );

  /* THE FILE'S CLOCK: the Records answer's, else the analytics answer's, else
     the device's — in that order, because the first two are the server's own
     statement of whose clock the numbers are on. */
  const analytics = data.batch.data || data.overview.data;
  const fileZone =
    usableZone(input.clock?.tz) ||
    usableZone(analytics?.tz) ||
    usableZone(window?.tz) ||
    usableZone(deviceZone()) ||
    'UTC';
  // The server's own label, verbatim; one is derived only where none was sent.
  const fileZoneLabel = input.clock?.label || zoneNote(fileZone, at) || fileZone;
  const generatedLabel = `${dateTimeIn(at.toISOString(), fileZone)} — ${fileZoneLabel}`;

  const meta: string[][] = [
    ['Generated at', generatedLabel],
    ['Times shown in', fileZoneLabel],
    ...describeFilter(filter, filterMeta).map(line => {
      const [label, ...rest] = line.split(': ');

      return [label, rest.join(': ')];
    }),
  ];

  // The period as the DAYS the server resolved (absent when no window was asked).
  if (isDayKey(input.clock?.fromDay) && isDayKey(input.clock?.toDay)) {
    meta.push([
      'Days covered',
      `${formatDayKey(input.clock?.fromDay as string, DAY_FULL)} to ${formatDayKey(
        input.clock?.toDay as string,
        DAY_FULL
      )}`,
      `${input.clock?.fromDay} to ${input.clock?.toDay}`,
    ]);
  }

  if (window) {
    meta.push([
      'Window',
      `${dateTimeIn(window.from, window.tz)} to ${dateTimeIn(window.to, window.tz)}`,
      zoneNote(window.tz, at) || window.tz,
    ]);
  }
  if (scope) {
    meta.push([
      'Scope',
      scope.all ? 'the whole organisation' : 'assigned buildings only',
      `${countOf(scope.buildings, 'building')} visible`,
      `enforcement ${scope.enforced ? 'on' : 'OFF'}`,
    ]);
    // THE WARNING GOES IN THE FILE. A scoped-looking export over unscoped data
    // is a false statement about a security control, and the file outlives the
    // screen that could have shown the alert.
    if (scope.note) meta.push(['WARNING', scope.note]);
    if (scope.zones.length) {
      meta.push([
        'Building time zones in scope',
        Array.from(new Set(scope.zones.map(zone => zoneLabel(zone) || zone))).join('; '),
      ]);
    }
  }

  const sections: ExportSection[] = [];

  /* ── THE ROWS, in the columns and the order that are on screen ── */
  const recordColumns = columnsFor(filter.book, columns, filter);
  if (!input.panelId) {
    /* Text for a cell. `createdAt` is ALREADY wall-clock text on the file's
       clock and is written as given. Any other date column is an instant, and
       leaves as text in the SAME zone — never as raw ISO, which is UTC and sat
       beside local text in one row. */
    const exportCell = (column: (typeof recordColumns)[number], row: RecordRow): string => {
      const raw = row[column.key];
      if (column.kind === 'date' && column.key !== 'createdAt' && raw) {
        return wallTextIn(String(raw), fileZone, { seconds: true }) || cellText(column, raw);
      }

      return cellText(column, raw);
    };
    // Offered only when it says something the main column does not.
    const buildingTime = rows.some(row => onAnotherClock(row, fileZone));

    sections.push({
      title: `${BOOK_LABELS[filter.book]} — records`,
      caption:
        'Exactly the rows the Records tab is showing under these filters, in the same columns and the same order. ' +
        `Times are shown in ${fileZoneLabel}.` +
        (buildingTime
          ? ' "Building time" is the same moment on the clock of the building the row belongs to.'
          : ''),
      header: [
        ...recordColumns.map(c => c.label),
        ...(buildingTime ? ['Building time'] : []),
      ],
      rows: rows.map(row => [
        ...recordColumns.map(column => exportCell(column, row)),
        ...(buildingTime ? [buildingTimeCell(row)] : []),
      ]),
    });
  }

  /* ── EVERY FIGURE, with the Counted column that keeps three different kinds
     of number apart. Without it a window total, a standing shelf figure and a
     live count sit in one list looking identical. ── */
  const overview = data.overview.data;
  if (overview && !input.panelId) {
    sections.push({
      title: 'Figures for this period',
      header: ['Figure', 'Value', 'Previous', 'Change', 'Counted'],
      rows: Object.entries(overview.kpis).map(([key, kpi]) => [
        key,
        String(kpi.value),
        kpi.compareValue == null ? '' : String(kpi.compareValue),
        kpi.delta == null ? '' : String(kpi.delta),
        kpi.kind,
      ]),
    });
    for (const note of overview.notes || []) {
      sections.push({ title: 'Note', header: [], rows: [[note]] });
    }
  }

  /* ── THE PANELS THAT ARE NOT BATCH RESULTS. Dwell, data quality, turnover,
     rhythm, the shelf and "every figure" draw from their own slices, so the
     batch loop below never matched their ids and their "Download the numbers
     behind …" produced a cover page and no numbers (exports-filters suite,
     2026-09-23). Each now exports exactly what its panel draws. ── */
  if (input.panelId && SLICE_PANELS[input.panelId]) {
    sections.push(...SLICE_PANELS[input.panelId](data));
  }

  /* ── ONE SECTION PER PANEL, from the SAME conversion the chart and the table
     use, so a sheet and a chart cannot disagree. ── */
  const measureLabel =
    filterMeta?.measures.find(m => m.key === filter.measure)?.label || filter.measure;
  const titles = panelTitles(measureLabel);

  for (const [id, result] of Object.entries(results)) {
    if (!result) continue;
    if (input.panelId && id !== input.panelId) continue;
    // Sparklines are the same numbers as the panel they sit under.
    if (id.startsWith('spark-')) continue;

    const title = titles[id] || id;
    if (isBatchFailure(result)) {
      // A panel that could not be answered SAYS SO rather than being omitted —
      // an omitted panel reads as a panel with nothing in it.
      sections.push({
        title,
        header: [],
        rows: [['could not be calculated', result.error]],
      });
      continue;
    }

    sections.push({
      title,
      caption: result.likeForLike
        ? undefined
        : 'The two periods are different lengths, so no percentage is valid.',
      header: [format.humanise(result.dimension), result.label, 'Previous', 'Change'],
      rows: result.items.map(item => [
        item.label,
        result.agg === 'max_at'
          ? // An instant: text on the answer's clock, not raw ISO.
            wallTextIn(
              typeof item.value === 'string' ? item.value : null,
              usableZone(result.clockZone) || fileZone
            ) || String(item.value ?? '')
          : String(format.numberOf(item.value) ?? 0),
        item.compareValue == null ? '' : String(item.compareValue),
        item.delta == null ? '' : String(item.delta),
      ]),
    });
    for (const note of result.notes || []) {
      sections.push({ title: `${title} — note`, header: [], rows: [[note]] });
    }
  }

  const name = input.panelId
    ? `lobbero-${slug(input.panelId)}-${stamp(at)}`
    : `lobbero-${filter.tab}-${slug(BOOK_LABELS[filter.book])}-${scopeSlug}-${slug(
        filter.range
      )}-${stamp(at)}`;

  return {
    filename: name,
    title: `Lobbero — ${BOOK_LABELS[filter.book]}`,
    generatedAt: at.toISOString(),
    generatedLabel,
    meta,
    truncation: input.truncated
      ? `Only the first ${(input.rowLimit || rows.length).toLocaleString()} rows the ` +
        'server would return are in this file. Every count derived from them is a ' +
        'floor, not a total. Narrow the period or the building to see the rest.'
      : undefined,
    sections,
  };
};

/** Columns available for a book, for the column menu and for the tests. */
export const allColumnsFor = (filter: DashboardFilter): string[] =>
  RECORD_COLUMNS[filter.book].map(c => c.key);
